<template>
  <PreviewModal
    v-if="isMobile"
    :pending="pending"
    :error="error"
    :result="result"
    :show-class-highlights="showClassHighlights"
    @close="$emit('update:showingPreview', false)"
  />
  <template v-else>
    <div
      class="relative flex grow flex-col @container dark:bg-sidebar"
      v-bind="$attrs"
    >
      <div
        v-if="!props.playground"
        class="flex h-[53px] min-w-0 shrink-0 items-center justify-between gap-4 border-b border-b-transparent bg-background px-4 transition"
        :class="{ '!border-border': emailPreview?.isScrolled }"
      >
        <div class="flex min-w-0 items-center gap-2">
          <div v-if="isMobile">
            <HeaderAction
              icon="arrow-left"
              label="Back"
              variant="secondary"
              @click="$emit('update:showingPreview', false)"
            />
          </div>

          <span
            class="truncate text-sm font-semibold text-black dark:text-white"
          >
            Preview
          </span>
        </div>
        <div class="-mr-2 flex min-w-0 shrink-0 items-center gap-2">
          <HeaderAction
            v-if="props.draftTo"
            icon="eye"
            label="Send draft"
            variant="secondary"
            :to="props.draftTo"
          />

          <Menu.Dropdown v-if="props.contexts.length > 1" label="Context">
            <Menu.CheckboxItem
              v-for="context in props.contexts"
              :key="context"
              :model-value="mode === context"
              :icon="CONTEXT_TO_CONFIGURATION[context].icon"
              :label="CONTEXT_TO_CONFIGURATION[context].title"
              variant="radio"
              @update:model-value="mode = context"
            />
          </Menu.Dropdown>

          <Menu.Dropdown
            v-if="props.showEmailSelector && emails.length > 0"
            :label="selectedEmailLabel"
          >
            <Menu.CheckboxItem
              :model-value="selectedEmailId === undefined"
              label="Latest email"
              variant="radio"
              @update:model-value="selectedEmailIdModel = undefined"
            />
            <Menu.CheckboxItem
              v-for="email in emails"
              :key="email.id"
              :model-value="selectedEmailId === email.id"
              :label="email.subject || '(no subject)'"
              variant="radio"
              @update:model-value="selectedEmailIdModel = email.id"
            />
          </Menu.Dropdown>

          <Select
            v-if="props.showSubscriberSelector"
            v-model:selection="selectedSubscriberIdModel"
            v-model:query="subscriberSearchQuery"
            :max-selection="1"
            close-on-select
            :pending="subscriberList.isFetching.value"
            :options="
              subscribers.map((subscriber) => ({
                id: subscriber.id,
                label: subscriber.email_address || 'Subscriber',
              }))
            "
            align="end"
          >
            <template #trigger>
              <Tooltip.Hint text="Select a subscriber to preview their email.">
                <Menu.TriggerButton>
                  {{
                    subscribers.length > 0
                      ? subscribers.find(
                          (s) => s.id === selectedSubscriberIdModel[0]
                        )?.email_address || "Subscriber"
                      : "Subscriber"
                  }}
                </Menu.TriggerButton>
              </Tooltip.Hint>
            </template>
            <template #empty-state>
              <div class="flex h-20 flex-col items-center justify-center">
                <p class="text-sm text-gray-400">
                  {{
                    subscriberSearchQuery.trim()
                      ? "No results found"
                      : "No subscribers"
                  }}
                </p>
              </div>
            </template>
          </Select>
        </div>
      </div>
      <EmailPreview
        ref="emailPreview"
        :mode="mode"
        :pending="pending"
        :error="error"
        :result="result"
        :contexts="props.contexts"
        :show-class-highlights="showClassHighlights"
        :scroll-sync-enabled="scrollSyncEnabled"
        :class="{
          'transition-all': isMounted,
        }"
      />
    </div>

    <PreviewModal
      v-if="openExpandedDialog"
      :pending="pending"
      :error="error"
      :result="result"
      :contexts="props.contexts"
      :show-class-highlights="showClassHighlights"
      @close="openExpandedDialog = false"
    />
  </template>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, useTemplateRef, watch } from "vue";

import Menu from "@/design_system/Menu";
import HeaderAction from "@/design_system/Page/HeaderAction.vue";
import Select from "@/design_system/Select";
import Tooltip from "@/design_system/Tooltip";
import { useEmailSearchQuery } from "@/lib/api/queries/emails/list";
import type { RenderResult } from "@/lib/api/queries/emails/render";
import { useSubscribersListingQuery } from "@/lib/api/queries/subscribers/list";
import { useMediaQuery } from "@/lib/composables/media-query";
import type { RouteLocation } from "@/router/types";
import {
  type PreviewContext,
  useStore as useUiStatesStore,
} from "@/store/stores/ui-states";

import EmailPreview from "./previews/EmailPreview.vue";
import PreviewModal from "./previews/PreviewModal.vue";
import { CONTEXT_TO_CONFIGURATION } from "./previews/types";

const emailPreview =
  useTemplateRef<InstanceType<typeof EmailPreview>>("emailPreview");

// Immediately setting `transition-all` seems to result in transitioning on
// mount, we only want transitions to happen on changing breakpoints.
const isMounted = ref(false);

const isMobile = useMediaQuery("(pointer: coarse) and (width < 768px)");
const openExpandedDialog = ref(false);

const props = withDefaults(
  defineProps<{
    pending?: boolean;
    error?: unknown;
    result: RenderResult | undefined;
    scrollSyncEnabled?: boolean;

    contexts?: PreviewContext[];
    draftTo?: RouteLocation;
    playground?: boolean;
    showClassHighlights?: boolean;
    showEmailSelector?: boolean;
    selectedEmailId?: string;
    showSubscriberSelector?: boolean;
    selectedSubscriberId?: string;
  }>(),
  {
    contexts: () => ["email", "web"],
    showEmailSelector: false,
    showSubscriberSelector: false,
  }
);

const uiStates = useUiStatesStore();

const mode = computed<PreviewContext>({
  get: () => {
    const stored = uiStates.previewContext;
    if (stored && props.contexts.includes(stored)) {
      return stored;
    }
    return props.contexts[0] ?? "email";
  },
  set: (v) => {
    uiStates.previewContext = v;
  },
});

// Ensure mode is valid when contexts change
watch(
  () => props.contexts,
  () => {
    if (!props.contexts.includes(mode.value)) {
      mode.value = props.contexts[0];
    }
  },
  { immediate: true }
);

const selectedEmailIdModel = computed({
  get: () => props.selectedEmailId,
  set: (value) => emit("update:selectedEmailId", value),
});
const selectedSubscriberIdModel = computed<string[]>({
  get: () => (props.selectedSubscriberId ? [props.selectedSubscriberId] : []),
  set: (value) => emit("update:selectedSubscriberId", value[value.length - 1]),
});

const emit = defineEmits<{
  "update:showingPreview": [value: boolean];
  "update:selectedEmailId": [value: string | undefined];
  "update:selectedSubscriberId": [value: string | undefined];
}>();

// Fetch emails for selector
const emailSearch = useEmailSearchQuery({
  q: "",
  ordering: "-publish_date",
  status: ["sent", "imported"],
  enabled: computed(() => props.showEmailSelector ?? false),
  maintainData: true,
});

const emails = computed(() => {
  return emailSearch.flattenedData.value.slice(0, 10); // Limit to 10 most recent
});

const selectedEmailLabel = computed(() => {
  if (selectedEmailIdModel.value === undefined) {
    return "Email";
  }
  const email = emails.value.find((e) => e.id === selectedEmailIdModel.value);
  return email?.subject || "Email";
});

// Search query for subscribers
const subscriberSearchQuery = ref("");

// Fetch subscribers for selector
const subscriberList = useSubscribersListingQuery({
  parameters: computed(() => {
    const trimmed = subscriberSearchQuery.value.trim();
    return {
      email_address: trimmed ? [trimmed] : undefined,
    };
  }),
  ordering: "-creation_date",
  maintainData: true,
});

const subscribers = computed(() => {
  // If searching, show all results; otherwise limit to 10 most recent
  const limit = subscriberSearchQuery.value.trim() ? undefined : 10;
  return limit
    ? subscriberList.flattenedData.value.slice(0, limit)
    : subscriberList.flattenedData.value;
});

onMounted(() => {
  // Seems like we need to wait one more tick for everything to settle in.
  requestAnimationFrame(() => {
    isMounted.value = true;
  });
});
</script>
